import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import {
  filterHeaderSearchCatalog,
  headerSearchCatalogEntryLabel,
  isAllowedHeaderSearchNavPath,
  type HeaderSearchCatalogEntry,
} from '@/utils/omniHeaderSearchCatalog';
import {
  getOmniSearchRecents,
  addOmniSearchRecentNav,
  addOmniSearchRecentCreatorQuery,
  addOmniSearchRecentTeam,
  clearOmniSearchRecents,
} from '@/utils/omniSearchRecents';
import { getTokenFromStorage, updateAuthInStorage } from '@/utils/auth';
import { isAdminJwtToken } from '@/utils/jwtTokenType';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamsSearch } from '@/hooks/useTeamsSearch';
import { sortClientTeamsByName } from '@/utils/sortClientTeamsByName';
import { selectClientTeam } from '@/api/beatlyApi';
import { showError } from '@/utils/alert';

type PanelRow =
  | { kind: 'catalog'; entry: HeaderSearchCatalogEntry }
  | { kind: 'recent-nav'; path: string; label: string }
  | { kind: 'recent-creator'; query: string }
  | { kind: 'team'; teamId: string; name: string }
  | { kind: 'fallback-creator'; query: string };

export type HeaderSearchBarProps = {
  /**
   * When false, catalog matches default client sidebar (no Brand Pulse / full cowork catalog).
   * Evolve destinations in search follow the signed-in user (`@beatly.com` → shown).
   */
  showCowork?: boolean;
  /** Same source as App sidebar “Last campaign” — enables campaign shortcuts in search. */
  lastCampaignPath?: string | null;
  lastCampaignDisplayName?: string | null;
};

export function HeaderSearchBar({
  showCowork = false,
  lastCampaignPath = null,
  lastCampaignDisplayName = null,
}: HeaderSearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();
  const { data: teamsSearchData } = useTeamsSearch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentsVersion, setRecentsVersion] = useState(0);
  const [teamSwitchLoading, setTeamSwitchLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAdminJwt = isAdminJwtToken(getTokenFromStorage());
  const showEvolveNav = user?.show_evolve === true;

  const recents = useMemo(() => {
    return getOmniSearchRecents().filter((r) => {
      if (r.kind === 'team') return Boolean(user?.is_client);
      if (r.kind === 'nav')
        return isAllowedHeaderSearchNavPath(r.path, { isAdminJwt, showCowork, showEvolveNav });
      return true;
    });
  }, [open, recentsVersion, isAdminJwt, showCowork, showEvolveNav, user?.is_client]);

  const catalogMatches = useMemo(
    () =>
      filterHeaderSearchCatalog(query, (k) => t(k), {
        includeDevMode: isAdminJwt,
        showCowork,
        showEvolveNav,
        lastCampaignPath,
        lastCampaignDisplayName,
      }).slice(0, 8),
    [query, t, isAdminJwt, showCowork, showEvolveNav, lastCampaignPath, lastCampaignDisplayName]
  );

  const teamMatches = useMemo(() => {
    if (!user?.is_client) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const teams = sortClientTeamsByName(teamsSearchData?.teams ?? []);
    return teams
      .filter((row) => {
        const name = (row.name ?? '').trim().toLowerCase();
        const id = (row.team_id ?? '').trim().toLowerCase();
        return name.includes(q) || id.includes(q);
      })
      .slice(0, 6);
  }, [user?.is_client, query, teamsSearchData?.teams]);

  const panelRows: PanelRow[] = useMemo(() => {
    const q = query.trim();
    if (!q) {
      return recents.map((r): PanelRow => {
        if (r.kind === 'nav') return { kind: 'recent-nav', path: r.path, label: r.label };
        if (r.kind === 'team') return { kind: 'team', teamId: r.teamId, name: r.label };
        return { kind: 'recent-creator', query: r.query };
      });
    }
    const rows: PanelRow[] = teamMatches.map((row) => ({
      kind: 'team' as const,
      teamId: row.team_id,
      name: (row.name ?? row.team_id).trim() || row.team_id,
    }));
    for (const entry of catalogMatches) {
      rows.push({ kind: 'catalog', entry });
    }
    rows.push({ kind: 'fallback-creator', query: q });
    return rows;
  }, [query, recents, catalogMatches, teamMatches]);

  const showPanel = open && (query.trim() ? true : focused);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, panelRows.length, recentsVersion]);

  useEffect(() => {
    if (selectedIndex >= panelRows.length) {
      setSelectedIndex(Math.max(0, panelRows.length - 1));
    }
  }, [selectedIndex, panelRows.length]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const bumpRecents = useCallback(() => {
    setRecentsVersion((v) => v + 1);
  }, []);

  const runTeamSwitch = useCallback(
    async (teamId: string, displayName: string) => {
      if (teamId === user?.team?.team_id) {
        setOpen(false);
        setQuery('');
        setFocused(false);
        return;
      }
      const token = getTokenFromStorage();
      if (!token || !setUser) return;
      setTeamSwitchLoading(true);
      try {
        const { token: newToken, user: updatedUser } = await selectClientTeam(teamId, token);
        updateAuthInStorage({ token: newToken, user: updatedUser });
        setUser(updatedUser);
        addOmniSearchRecentTeam(teamId, displayName);
        await queryClient.invalidateQueries({ queryKey: ['client', 'teams', 'search'] });
        bumpRecents();
        setOpen(false);
        setQuery('');
        setFocused(false);
      } catch {
        showError(t('campaigns.teamSwitchError'));
      } finally {
        setTeamSwitchLoading(false);
      }
    },
    [user?.team?.team_id, setUser, queryClient, bumpRecents, t]
  );

  const runRow = useCallback(
    (row: PanelRow) => {
      if (row.kind === 'team') {
        void runTeamSwitch(row.teamId, row.name);
        return;
      }
      if (row.kind === 'catalog') {
        const label = headerSearchCatalogEntryLabel(row.entry, (k) => t(k));
        addOmniSearchRecentNav(row.entry.path, label);
        navigate(row.entry.path);
      } else if (row.kind === 'recent-nav') {
        addOmniSearchRecentNav(row.path, row.label);
        navigate(row.path);
      } else if (row.kind === 'recent-creator') {
        addOmniSearchRecentCreatorQuery(row.query);
        navigate(`/creators/search?q=${encodeURIComponent(row.query)}`);
      } else {
        addOmniSearchRecentCreatorQuery(row.query);
        navigate(`/creators/search?q=${encodeURIComponent(row.query)}`);
      }
      bumpRecents();
      setOpen(false);
      setQuery('');
      setFocused(false);
    },
    [navigate, t, bumpRecents, runTeamSwitch]
  );

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (teamSwitchLoading) return;
    const q = query.trim();
    if (panelRows.length > 0 && panelRows[selectedIndex]) {
      runRow(panelRows[selectedIndex]);
      return;
    }
    if (q) {
      addOmniSearchRecentCreatorQuery(q);
      navigate(`/creators/search?q=${encodeURIComponent(q)}`);
      bumpRecents();
      setOpen(false);
      setQuery('');
      setFocused(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      setFocused(false);
      return;
    }
    if (teamSwitchLoading) return;
    if (!showPanel || panelRows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % panelRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + panelRows.length) % panelRows.length);
    }
  };

  const handleFocus = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setFocused(true);
  };

  const handleBlur = () => {
    blurTimerRef.current = setTimeout(() => setFocused(false), 120);
  };

  const panelMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const labelForRow = (row: PanelRow): string => {
    if (row.kind === 'catalog') return headerSearchCatalogEntryLabel(row.entry, (k) => t(k));
    if (row.kind === 'recent-nav') return row.label;
    if (row.kind === 'team') return t('headerSearch.teamRow', { name: row.name });
    if (row.kind === 'recent-creator') {
      return t('headerSearch.recentCreatorRow', { query: row.query });
    }
    return t('headerSearch.fallbackCreatorSearch', { query: row.query });
  };

  const showEmptyHint = open && focused && !query.trim() && recents.length === 0;

  return (
    <div ref={containerRef} className="relative mr-1">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          title={t('nav.search')}
          aria-expanded={false}
          aria-label={t('nav.search')}
        >
          <Search className="w-[18px] h-[18px]" />
        </button>
      ) : (
        <>
          <form
            onSubmit={submitForm}
            className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 min-w-[14rem] sm:min-w-[18rem]"
          >
            <Search className="w-4 h-4 text-gray-600 shrink-0" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t('headerSearch.placeholder')}
              className="bg-transparent text-sm text-gray-900 outline-none flex-1 min-w-0 placeholder:text-gray-600"
              aria-label={t('nav.search')}
              aria-autocomplete="list"
              aria-controls="header-search-suggestions"
              aria-expanded={showPanel}
              role="combobox"
            />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setQuery('');
                setFocused(false);
              }}
              className="text-gray-600 hover:text-gray-900"
              aria-label={t('common.close')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </form>

          {showPanel && (
            <div
              id="header-search-suggestions"
              role="listbox"
              aria-label={t('headerSearch.suggestionsLabel')}
              onMouseDown={panelMouseDown}
              className={cn(
                'absolute top-full right-0 mt-1 z-[60] w-[min(22rem,calc(100vw-2rem))] max-h-64 overflow-y-auto',
                'rounded-lg border border-gray-200 bg-white py-1 shadow-lg'
              )}
            >
              {showEmptyHint && (
                <p className="px-3 py-2 text-xs text-gray-600 leading-snug border-b border-gray-100">
                  {t('headerSearch.hintFocus')}
                </p>
              )}

              {!query.trim() && recents.length > 0 && (
                <div className="flex items-center justify-between px-2 pt-1 pb-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
                    {t('headerSearch.recentHeading')}
                  </span>
                  <button
                    type="button"
                    className="text-[11px] text-[#be43ff] hover:underline px-1"
                    onClick={() => {
                      clearOmniSearchRecents();
                      bumpRecents();
                    }}
                  >
                    {t('headerSearch.clearRecents')}
                  </button>
                </div>
              )}

              {panelRows.map((row, index) => (
                <button
                  key={
                    row.kind === 'catalog'
                      ? `c-${row.entry.id}`
                      : row.kind === 'recent-nav'
                        ? `rn-${row.path}-${index}`
                        : row.kind === 'team'
                          ? `tm-${row.teamId}-${index}`
                          : row.kind === 'recent-creator'
                            ? `rc-${row.query}-${index}`
                            : `fb-${row.query}`
                  }
                  type="button"
                  role="option"
                  aria-selected={index === selectedIndex}
                  disabled={teamSwitchLoading}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    teamSwitchLoading ? 'opacity-60 pointer-events-none' : '',
                    index === selectedIndex ? 'bg-[#be43ff]/10 text-gray-900' : 'text-gray-800 hover:bg-gray-50'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onClick={() => runRow(row)}
                >
                  {row.kind === 'fallback-creator' ? (
                    <span className="text-gray-700">
                      {t('headerSearch.fallbackCreatorSearch', { query: row.query })}
                    </span>
                  ) : (
                    <span>{labelForRow(row)}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
